import React from "react";
import { Table } from "component/shared/table";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { formatPrice } from "shared/price-formatter";
import { SavedSearch } from "model/saved-search";
import { CriterionComponent } from "component/shared/criterion-component";
import { styles } from "./style";
import { FeatherIcon } from "component/shared/feather-icon";
import moment from "moment";
import { dateFormatter } from "shared/date-formatter";
import { mixpanelAnalytics } from "shared/mixpanel-analytics";
import * as env from "shared/env";

interface Props extends
	WithStyles<typeof styles>
{
	savedSearch: SavedSearch;
	index: number;
	showPropertyOrganizer: Function;
	leadId: number;
}

const Component = (props: Props) => {
	const { savedSearch, showPropertyOrganizer, leadId } = props;
	const [expanded, setExpanded] = React.useState(false);

	const handleExpansionChange = (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
		setExpanded(isExpanded);
		if (isExpanded) {
			mixpanelAnalytics.trackViewEmailAlertActivity(leadId);
		}
	};
	const monthAgo = moment().subtract(1, "M");
	const sentCount = (savedSearch.emailSents) ? savedSearch.emailSents.filter(emailSent => emailSent.sentOn > monthAgo).length : 0;
	const emailClicks = (savedSearch.emailClicks) ? savedSearch.emailClicks : [];
	return (
		<Mui.Accordion expanded={expanded} onChange={handleExpansionChange}>
			<Mui.AccordionSummary
				expandIcon={
					<FeatherIcon>
						<Icons.ChevronDown />
					</FeatherIcon>
				}
			>
				<Mui.Grid container justifyContent="space-between">
					<Mui.Grid item>
						<Mui.Typography variant="h4">
							{savedSearch.label}
						</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						{!savedSearch.inactive && 
							<Mui.Chip
								label={savedSearch.recieveEmails ? "Active" : "Inactive"}
								size="small"
								variant="outlined"
								color={savedSearch.recieveEmails ? "secondary" : "default"}
							/>
						}
						{savedSearch.inactive && 
							<Mui.Chip
								label="Deleted"
								size="small"
							/>
						}
					</Mui.Grid>
				</Mui.Grid>
			</Mui.AccordionSummary>
			<Mui.AccordionDetails>
				<Mui.Grid container direction="column" spacing={2}>
					<Mui.Grid item>
						<Mui.Typography align="right">
							{sentCount} {sentCount === 1 ? "email" : "emails"} sent in the past 30 days
						</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<CriterionComponent criteria={savedSearch.criteria} />
					</Mui.Grid>
					<Mui.Grid item>
						<Table
							className="-highlight"
							items={emailClicks}
							columns={[
								{
									id: "clickedOn",
									title: "Date Clicked",
									width: 165,
									value: (emailClick) => {
										return (
											<Mui.Typography>
												{dateFormatter(emailClick.clickedOn)}
											</Mui.Typography>
										);
									},
									style: {
										alignItems: "flex-start",
									},
								},
								{
									id: "listingAddress",
									title: "Opened Listing",
									value: (emailClick) => {
										return (
											<Mui.Typography>
												{emailClick.listing ? (
													<Mui.Link href={emailClick.listing.detailUrl} target="_blank">
														{emailClick.listing.addressOneLine}
													</Mui.Link>
												) : (
													"--"
												)}
											</Mui.Typography>
										);
									},
								},
								{
									id: "listingPrice",
									title: "Listing Price",
									width: 110,
									value: (emailClick) => {
										return (
											<Mui.Typography>
												{emailClick.listing ? (
													formatPrice(emailClick.listing.price)
												) : (
													"--"
												)}
											</Mui.Typography>
										);
									},
								},
							]}
							style={{
								width: "100%",
							}}
							minRows={0}
							defaultPageSize={5}
							showPagination={emailClicks.length > 5 ? true : false }
							NoDataComponent={() => (
								<Mui.Typography>No emails clicked yet.</Mui.Typography>
							)}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.AccordionDetails>
			<Mui.AccordionActions>
				<Mui.Button
					variant="text"
					color={"primary"}
					style={{ background: "none" }}
					disableRipple
					disableTouchRipple
					onClick={() => showPropertyOrganizer()}
				>
					{!savedSearch.inactive && 
						<Mui.Typography>Manage Email Alerts/Saved Searches</Mui.Typography>
					}
				</Mui.Button>
			</Mui.AccordionActions>
		</Mui.Accordion>
	);
};

export const ExpansionTable = withStyles(Component, styles);
